import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  IconButton,
  Alert,
  Stack,
  Paper,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  TableHead,
  Table,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import BuildIcon from "@mui/icons-material/Build";
import PersonIcon from "@mui/icons-material/Person";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SpeedIcon from "@mui/icons-material/Speed";
import WarningIcon from "@mui/icons-material/Warning";
import Visibility from "@mui/icons-material/Visibility";
import Description from "@mui/icons-material/Description";
import { VehicleExpenseResponse, vehicleExpenseApi } from "../apiService";
import { formatExpenseType, getExpenseTypeColor } from "../utils/expenseUtils";
import { formatCurrency, formatDate } from "../../../utils/utils";
import { fetchDocuments } from "../../../utils/FetchDocuments";

interface ExpenseDetailViewProps {
  open: boolean;
  expenseId: string;
  onClose: () => void;
  onEdit?: (expenseId: string) => void;
}

const ExpenseDetailView: React.FC<ExpenseDetailViewProps> = ({
  open,
  expenseId,
  onClose,
  onEdit,
}) => {
  const [expense, setExpense] = useState<VehicleExpenseResponse>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExpenseDetails = async () => {
      if (!expenseId) return;

      setLoading(true);
      setError(null);
      try {
        const expenseData = await vehicleExpenseApi.getExpense(expenseId);
        const documents = await fetchDocuments({
          documentType: "vehicle-expense",
          documentTypeId: expenseId,
          documentContext: "vehicle-expense",
        });
        expenseData.details.uploadedFiles = documents;
        setExpense(expenseData);
      } catch (err) {
        setError("Failed to fetch expense details. Please try again.");
        console.error("Error fetching expense details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (open && expenseId) {
      fetchExpenseDetails();
    }
  }, [open, expenseId]);

  const handleClose = () => {
    setExpense(undefined);
    setError(null);
    onClose();
  };

  if (!open) return null;

  const renderFuelDetails = () => {
    if (!expense) return null;
    if (!expense.details || expense.expenseType !== "FUEL") return null;

    const details = expense.details;

    // Calculate fuel efficiency and cost metrics
    const liters = parseFloat(details.liters) || 0;
    const pricePerLitre = parseFloat(details.pricePerLitre) || 0;
    const bulkDiscount = parseFloat(details.bulkDiscountApplied) || 0;
    const totalBeforeDiscount = liters * pricePerLitre;
    const discountAmount = liters * bulkDiscount;
    const finalAmount = totalBeforeDiscount - discountAmount;
    const effectivePricePerLitre = liters > 0 ? finalAmount / liters : 0;

    return (
      <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <LocalGasStationIcon sx={{ mr: 1, color: "success.main" }} />
          <Typography variant="body2" color="primary">
            Fuel Details
          </Typography>
        </Box>

        <Grid container spacing={1}>
          {/* Basic Fuel Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Fuel Type
            </Typography>
            <Chip
              label={details.fuelType || "N/A"}
              color="success"
              variant="outlined"
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Quantity
            </Typography>
            <Typography variant="body2">
              {details.liters ? `${details.liters} L` : "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Price per Litre
            </Typography>
            <Typography variant="body2">
              {details.pricePerLitre
                ? formatCurrency(details.pricePerLitre)
                : "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Bulk Discount
            </Typography>
            <Typography variant="body2" color="success.main">
              {details.bulkDiscountApplied
                ? `${formatCurrency(details.bulkDiscountApplied)}/L`
                : "None"}
            </Typography>
          </Grid>

          {/* Cost Breakdown */}
          {liters > 0 && pricePerLitre > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Cost Breakdown
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: "grey.50" }}>
                  <Stack spacing={1}>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">
                        Base Amount ({liters}L × {formatCurrency(pricePerLitre)}
                        )
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(totalBeforeDiscount)}
                      </Typography>
                    </Box>
                    {bulkDiscount > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="success.main">
                          Bulk Discount ({liters}L ×{" "}
                          {formatCurrency(bulkDiscount)})
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          -{formatCurrency(discountAmount)}
                        </Typography>
                      </Box>
                    )}
                    <Divider />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">Final Amount</Typography>
                      <Typography variant="body2" color="primary">
                        {formatCurrency(finalAmount)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Effective Price per Litre
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(effectivePricePerLitre)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </>
          )}

          {/* Additional Information */}
          {(details.odometerReading || details.petrolPumpId) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Additional Information
                </Typography>
              </Grid>
              {details.odometerReading && (
                <Grid item xs={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <SpeedIcon
                      sx={{ mr: 1, color: "info.main", fontSize: 20 }}
                    />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Odometer Reading
                      </Typography>
                      <Typography variant="body2">
                        {details.odometerReading.toLocaleString()} km
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Paper>
    );
  };

  const renderMaintenanceDetails = () => {
    if (!expense) return null;
    if (
      !expense.details ||
      !["MAINTENANCE", "REPAIR"].includes(expense.expenseType)
    )
      return null;

    const details = expense.details;
    const isRepair = expense.expenseType === "REPAIR";

    // Check if warranty is expiring soon (within 30 days)
    const warrantyEndDate = details.warrantyEndDate
      ? new Date(details.warrantyEndDate)
      : null;
    const today = new Date();
    const thirtyDaysFromNow = new Date(
      today.getTime() + 30 * 24 * 60 * 60 * 1000
    );
    const isWarrantyExpiringSoon =
      warrantyEndDate &&
      warrantyEndDate <= thirtyDaysFromNow &&
      warrantyEndDate > today;
    const isWarrantyExpired = warrantyEndDate && warrantyEndDate <= today;

    return (
      <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <BuildIcon
            sx={{ mr: 1, color: isRepair ? "error.main" : "warning.main" }}
          />
          <Typography variant="body2" color="primary">
            {isRepair ? "Repair" : "Maintenance"} Details
          </Typography>
        </Box>

        <Grid container spacing={1}>
          {/* Service Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Service Information
            </Typography>
          </Grid>
          {details.serviceType && (
            <Grid item xs={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Service Type
              </Typography>
              <Typography variant="body2">{details.serviceType}</Typography>
            </Grid>
          )}
          {details.serviceProviderType && (
            <Grid item xs={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Service Provider Type
              </Typography>
              <Chip
                label={details.serviceProviderType}
                color="info"
                variant="outlined"
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>
          )}
          {details.actionType && (
            <Grid item xs={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Action Performed
              </Typography>
              <Typography variant="body2">{details.actionType}</Typography>
            </Grid>
          )}

          {/* Item Information */}
          {(details.maintenanceItem || details.customMaintenanceItem) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Item Details
                </Typography>
              </Grid>
              {details.maintenanceItem && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Maintenance Item
                  </Typography>
                  <Typography variant="body2">
                    {details.maintenanceItem}
                  </Typography>
                </Grid>
              )}
              {details.customMaintenanceItem && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Custom Item
                  </Typography>
                  <Typography variant="body2">
                    {details.customMaintenanceItem}
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {/* Warranty Information */}
          {(details.warrantyPeriod ||
            details.warrantyStartDate ||
            details.warrantyEndDate) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Warranty Information
                </Typography>
              </Grid>
              {details.warrantyPeriod && (
                <Grid item xs={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Warranty Period
                  </Typography>
                  <Typography variant="body2">
                    {details.warrantyPeriod}
                  </Typography>
                </Grid>
              )}
              {details.warrantyStartDate && (
                <Grid item xs={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Warranty Start
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(details.warrantyStartDate)}
                  </Typography>
                </Grid>
              )}
              {details.warrantyEndDate && (
                <Grid item xs={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Warranty End
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(details.warrantyEndDate)}
                  </Typography>
                </Grid>
              )}

              {/* Warranty Status Alert */}
              {(isWarrantyExpiringSoon || isWarrantyExpired) && (
                <Grid item xs={12}>
                  <Alert
                    severity={isWarrantyExpired ? "error" : "warning"}
                    icon={<WarningIcon />}
                    sx={{ mt: 1 }}
                  >
                    {isWarrantyExpired
                      ? "Warranty has expired"
                      : "Warranty expires within 30 days"}
                  </Alert>
                </Grid>
              )}
            </>
          )}

          {/* Documentation */}
          {(details.invoiceNumber || details.workOrderNumber) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Documentation
                </Typography>
              </Grid>
              {details.invoiceNumber && (
                <Grid item xs={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ReceiptIcon
                      sx={{ mr: 1, color: "info.main", fontSize: 20 }}
                    />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Invoice Number
                      </Typography>
                      <Typography variant="body2">
                        {details.invoiceNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {details.workOrderNumber && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Work Order Number
                  </Typography>
                  <Typography variant="body2">
                    {details.workOrderNumber}
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {/* Additional Information */}
          {(details.odometerReading || details.nextServiceDue) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Additional Information
                </Typography>
              </Grid>
              {details.odometerReading && (
                <Grid item xs={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <SpeedIcon
                      sx={{ mr: 1, color: "info.main", fontSize: 20 }}
                    />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Odometer Reading
                      </Typography>
                      <Typography variant="body2">
                        {details.odometerReading.toLocaleString()} km
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {details.nextServiceDue && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Next Service Due
                  </Typography>
                  <Typography variant="body2">
                    {details.nextServiceDue.toLocaleString()} km
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Paper>
    );
  };

  const renderDriverCleanerDetails = () => {
    if (!expense) return null;
    if (!expense.details || expense.expenseType !== "DRIVER_CLEANER")
      return null;

    const details = expense.details;

    return (
      <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <PersonIcon sx={{ mr: 1, color: "info.main" }} />
          <Typography variant="body2" color="primary">
            Staff Details
          </Typography>
        </Box>

        <Grid container spacing={1}>
          {details.category && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Staff Type
              </Typography>
              <Chip
                label={details.category}
                color="info"
                variant="outlined"
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>
          )}
          {details.referenceId && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Staff ID
              </Typography>
              <Typography variant="body2">{details.referenceId}</Typography>
            </Grid>
          )}
          {details.customCategory && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Custom Category
              </Typography>
              <Typography variant="body2">{details.customCategory}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const renderParkingDetails = () => {
    if (!expense) return null;
    if (!expense.details || expense.expenseType !== "PARKING") return null;

    const details = expense.details;

    return (
      <Paper
        variant="outlined"
        sx={{
          mt: 1,
          p: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <LocalParkingIcon sx={{ mr: 1, color: "secondary.main" }} />
          <Typography variant="body2" color="primary">
            Parking Details
          </Typography>
        </Box>

        <Grid container spacing={1}>
          {details.category && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Parking Type
              </Typography>
              <Typography variant="body2">{details.category}</Typography>
            </Grid>
          )}
          {details.referenceId && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Parking Reference ID
              </Typography>
              <Typography variant="body2">{details.referenceId}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const renderOtherDetails = () => {
    if (!expense) return null;
    if (
      !expense.details ||
      !["OTHER", "INSURANCE"].includes(expense.expenseType)
    )
      return null;

    const details = expense.details;

    return (
      <Paper
        variant="outlined"
        sx={{
          mt: 1,
          p: 1,
        }}
      >
        <Typography variant="body2" gutterBottom color="primary">
          Category Details
        </Typography>
        <Grid container spacing={1}>
          {details.category && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Category
              </Typography>
              <Chip
                label={details.category}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>
          )}
          {details.customCategory && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Custom Category
              </Typography>
              <Typography variant="body2">{details.customCategory}</Typography>
            </Grid>
          )}
          {details.referenceId && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Reference ID
              </Typography>
              <Typography variant="body2">{details.referenceId}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const renderPaymentDetails = () => {
    if (!expense) return null;
    if (!expense.payments || expense.payments.length === 0) return null;

    const payments = expense.payments;

    return (
      <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
        <Typography variant="body2" gutterBottom color="primary">
          Payment Details
        </Typography>
        {/* table to show payments */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Received From</TableCell>
                <TableCell>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{payment.type}</TableCell>
                  <TableCell>{payment.mode}</TableCell>
                  <TableCell>{formatDate(payment.date)}</TableCell>
                  <TableCell>{formatCurrency(payment.amount)}</TableCell>
                  <TableCell>{payment.location || "-"}</TableCell>
                  <TableCell>{payment.receivedFrom || "-"}</TableCell>
                  <TableCell>{payment.remarks || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              Expense Details
            </Typography>
            <Chip
              label={formatExpenseType(expense?.expenseType || "")}
              color={getExpenseTypeColor(expense?.expenseType || "")}
              size="small"
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : !expense ? (
          <Alert severity="info">No expense details available.</Alert>
        ) : (
          <>
            {/* Basic Information */}
            <Paper variant="outlined" sx={{ mb: 1, p: 1 }}>
              <Typography variant="body2" gutterBottom color="primary">
                Basic Information
              </Typography>

              <Grid container>
                <Grid item xs={6} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Vehicle Number
                  </Typography>
                  <Typography variant="body2" color="primary">
                    {expense.vehicleNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Expense Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(expense.expenseDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body2" color="primary">
                    {formatCurrency(expense.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Paid To
                  </Typography>
                  <Typography variant="body2">{expense.paidTo}</Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body2">{expense.location}</Typography>
                </Grid>
                {expense.contactNumber && (
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      Contact Number
                    </Typography>
                    <Typography variant="body2">
                      {expense.contactNumber}
                    </Typography>
                  </Grid>
                )}
                {expense.notes && (
                  <Grid item xs={12} md={expense.contactNumber ? 2 : 4}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">{expense.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Type-specific details */}
            {renderFuelDetails()}
            {renderMaintenanceDetails()}
            {/* {renderDriverCleanerDetails()} */}
            {renderParkingDetails()}
            {renderOtherDetails()}
            {renderPaymentDetails()}

            {/* Audit Information */}
            <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
              <Typography variant="body2" gutterBottom color="primary">
                Audit Information
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={2}>
                  <Typography variant="body2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(expense.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(expense.updatedAt)}
                  </Typography>
                </Grid>
                {/* {expense?.createdBy?.name && (
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">
                      Created By
                    </Typography>
                    <Typography variant="body2">
                      {expense.createdBy.name}
                    </Typography>
                  </Grid>
                )} */}
              </Grid>
            </Paper>

            {expense.details.uploadedFiles &&
              expense.details.uploadedFiles.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
                  <Box>
                    <Typography variant="body2" gutterBottom color="primary">
                      Uploaded Expense Proofs
                    </Typography>
                    <Grid container spacing={1}>
                      {expense.details.uploadedFiles.map((file: any) => (
                        <Grid item xs={12} sm={4} key={file.id}>
                          <Button
                            onClick={() =>
                              window.open(file?.presignedUrl || "", "_blank")
                            }
                            variant="outlined"
                            startIcon={<Description sx={{ fontSize: 18 }} />}
                            sx={{
                              width: "100%",
                              justifyContent: "flex-start",
                              fontSize: "0.75rem",
                              textTransform: "none",
                              py: 1,
                              borderColor: "grey.300",
                            }}
                          >
                            {file.fileName}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ px: 2, py: 0.5 }} size="large">
          Close
        </Button>
        {onEdit && expense && (
          <Button
            variant="contained"
            onClick={() => onEdit(expense.id)}
            size="large"
            sx={{ px: 2, py: 0.5 }}
          >
            Edit Expense
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseDetailView;
